import { atom } from 'nanostores'
import { type MouseEvent, useEffect, useRef } from 'react'

const enum HoverState {
	None,
	Popover,
	Trigger
}

export function initHoverActions({ anchorCN }: { anchorCN: string }) {
	let hoverTimeOut = 0

	const $hover = atom<HoverState>(HoverState.None),
		$id = atom('')

	function handleTriggerHover(event: MouseEvent<HTMLButtonElement>) {
		hoverTimeOut && window.clearTimeout(hoverTimeOut)

		if (event.currentTarget.value === $id.get()) {
			$hover.set(HoverState.Trigger)
		} else {
			$hover.set(HoverState.None)
			queueMicrotask(() => $hover.set(HoverState.Trigger))
		}
	}

	function handleTriggerUnHover() {
		hoverTimeOut = window.setTimeout(() => {
			if ($hover.get() !== HoverState.Popover) {
				$hover.set(HoverState.None)
			}
		}, 300)
	}

	function handleTriggerClick(event: MouseEvent<HTMLButtonElement>) {
		const button = event.currentTarget
		const nextID = button.value

		button.classList.add('active-info')
		button.classList.add(anchorCN)

		$id.set(nextID)

		const unsubscribe = $id.listen(newId => {
			if (newId !== nextID) {
				button.classList.remove('active-info')
				button.classList.remove(anchorCN)
				unsubscribe()
			}
		})
	}

	function useSyncPopoverState() {
		const ref = useRef<HTMLDivElement | null>(null)
		useEffect(() => {
			const popover = ref.current

			if (!popover) {
				return
			}

			let timer: number

			function syncToggle(event: ToggleEvent) {
				timer && window.clearTimeout(timer)
				if (event.newState === 'closed') {
					timer = window.setTimeout(() => $id.set(''), 200)
				}
			}

			// @ts-expect-error
			popover.addEventListener('toggle', syncToggle)

			const unsubscribe = $id.subscribe(id => {
				if (id) {
					popover.showPopover()
				} else {
					popover.hidePopover()
				}
			})

			const unSubHover = $hover.listen(current => {
				if (current === HoverState.None) {
					popover.hidePopover()
				}
			})

			return () => {
				// @ts-expect-error
				popover.removeEventListener('toggle', syncToggle)
				unsubscribe()

				unSubHover()
			}
		}, [])

		return ref
	}

	function handlePopoverHover() {
		hoverTimeOut && window.clearTimeout(hoverTimeOut)
		$hover.set(HoverState.Popover)
	}

	function handlePopoverUnHover() {
		hoverTimeOut = window.setTimeout(() => {
			if ($hover.get() !== HoverState.Trigger) {
				$hover.set(HoverState.None)
			}
		}, 300)
	}

	return {
		$id,
		popoverListeners: {
			onMouseEnter: handlePopoverHover,
			onMouseLeave: handlePopoverUnHover,
			popover: 'auto'
		},
		triggerListeners: {
			onClick: handleTriggerClick,
			onMouseEnter: handleTriggerHover,
			onMouseLeave: handleTriggerUnHover
		},
		useSyncPopoverState
	}
}
